import { Paragraph } from 'components/elements/paragraph/Paragraph';
import { sanitizeHtml } from 'utils/sanitize';
import { useSessionContext } from 'utils/context/session-provider';
import { LocalStorageKeys, useLocalStorage } from 'utils/localStorage';
import { TroubleshootingCode } from 'components/elements/troubleshooting-code/troubleshooting-code';
import { StyledButton } from 'components/elements/button/Button.styles';
import { usePolyglotT } from 'utils/locale/locale';

export const TroubleshootingDetails = () => {
  const { t, helpLinks } = usePolyglotT();

  // If possible, display the public client id or correlation id so Support can find the session in logs
  // 1. try session context (gets them from URL)
  // 2. try local storage. Note that if values are in local storage and not session context, they may be stale.
  // When the values are set in storage, they are appended to the URL params so if these are out of sync, something has gone wrong.

  const { clientId, correlationId } = useSessionContext();

  const [storedClientId] = useLocalStorage<string>(LocalStorageKeys.clientId);
  const [storedCorrelationId] = useLocalStorage<string>(
    LocalStorageKeys.correlationId
  );

  const finalClientId = clientId || storedClientId || undefined;
  const finalCorrelationId = correlationId || storedCorrelationId || undefined;

  // Use PCI (public client id) for preference, or correlation id if PCI is not available
  const errorId = finalClientId || finalCorrelationId || undefined;

  // The label prop is not translated because it tells Support which code has been provided
  // and matches the field name in the logs
  const errorIdLabel = finalClientId ? 'Public Client ID' : 'Correlation ID';

  const helpCentreUrl = helpLinks.help_centre_account_access;

  const errorIdText = errorId
    ? t('troubleshooting_details.description_with_error_id_raise_ticket')
    : t('troubleshooting_details.description_no_error_id_raise_ticket');

  return (
    <div>
      <Paragraph
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(errorIdText) }}
      />
      {errorId && <TroubleshootingCode code={errorId} label={errorIdLabel} />}
      {/* this is rendered as a link so that it is semantically a link, however we want it to look like a button */}
      <StyledButton
        as='a'
        href={helpCentreUrl}
        target='_blank'
        rel='noopener noreferrer'
        variant='primary'
        isFull={true}
        style={{
          display: 'inline-block',
          marginTop: '16px',
          textDecoration: 'none',
        }}
      >
        {t('troubleshooting_details.button_raise_ticket')}
      </StyledButton>
    </div>
  );
};
